import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faDroplet, faLeaf, faTemperatureHalf, faClock, faWater } from '@fortawesome/free-solid-svg-icons';
import { formatRelativeDate } from '../utils/date';
import { useNavigate } from 'react-router-dom';
import LocateButton from './LocateButton';
import { useAlertAcknowledgements } from '../hooks/useAlertAcknowledgements';
import { plantScoreClass, SCORE_HINT } from '../utils/plantScore';

const SENSORS = [
        { type: 'temp', icon: faTemperatureHalf },
        { type: 'light', icon: faSun },
        { type: 'moist', icon: faDroplet },
        { type: 'nutrition', icon: faLeaf },
];

const DeviceListItem = ({ device, onLocate, dragProps }) => {
        const navigate = useNavigate();
        const { isAcknowledged, acknowledge } = useAlertAcknowledgements(device.id);

        const getAlertForSensor = (sensorType) => {
                if (!device.alerts || device.alerts.length === 0) return null;
                return device.alerts.find(a => a.sensor_type === sensorType) || null;
        };

        const getSensorStatus = (sensorType) => {
                if (!device.updated_at) {
                        return { label: '--', className: 'bg-gray-100 text-gray-500' };
                }
                const alert = getAlertForSensor(sensorType);
                if (alert && isAcknowledged(alert)) {
                        return { label: alert.action_title, className: 'bg-yellow-100 text-yellow-800' };
                }
                if (alert) {
                        return { label: alert.action_title, className: 'bg-red-100 text-red-800' };
                }
                return { label: 'Good', className: 'bg-green-50 text-green-800' };
        };

        const getWaterStatus = () => {
                if (device.water_level < 10) {
                        return { className: 'text-red-500' };
                }
                return { className: 'text-blue-500' };
        };

        const score = device.updated_at ? device.score ?? null : null;
        const dateTime = device.updated_at ? formatRelativeDate(device.updated_at) : 'Never';

        var imgSrc;
        if (device.image === null) {
                imgSrc = 'https://storage.googleapis.com/uxpilot-auth.appspot.com/e4c5def903-e02e5cf35c228c7398d8.png'
        } else {
                imgSrc = `/api/v2/devices/${device.id}/image`;
        }

        return (
                <div
                        className="grid items-center bg-white rounded-xl px-4 py-3 card-shadow cursor-pointer hover:shadow-2xl transition-all duration-200 text-sm"
                        style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'subgrid' }}
                        onClick={() => navigate(`/device/${device.id}`)}
                        data-testid="device-list-item"
                        data-device-id={device.id}
                        data-device-name={device.name}
                        {...dragProps}
                >
                        {/* Image + Name */}
                        <div className="flex items-center gap-3">
                                <img className="w-12 h-12 rounded-lg object-cover flex-shrink-0" src={imgSrc} alt="plant" />
                                <LocateButton device={device} onLocate={onLocate} className="text-sm" />
                                <span data-testid="device-list-name" className="font-semibold text-text-strong whitespace-nowrap">{device.name}</span>
                        </div>

                        {/* Sensors group */}
                        <div className="flex items-center gap-1 pl-4 pr-64">
                                {SENSORS.map(({ type, icon }) => {
                                        const status = getSensorStatus(type);
                                        const alert = device.updated_at ? getAlertForSensor(type) : null;
                                        const chipClass = `inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${status.className}`;
                                        const content = <><FontAwesomeIcon icon={icon} className="mr-1" />{status.label}</>;
                                        // An open alert is acknowledged by clicking its chip; once
                                        // acknowledged it stays yellow and can't be undone.
                                        if (alert && !isAcknowledged(alert)) {
                                                return (
                                                        <button
                                                                key={type}
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); acknowledge(alert); }}
                                                                title="Click to acknowledge"
                                                                data-testid={`${type}-status`}
                                                                data-acknowledged="false"
                                                                className={`${chipClass} hover:ring-2 hover:ring-yellow-300 transition-shadow`}
                                                        >
                                                                {content}
                                                        </button>
                                                );
                                        }
                                        return (
                                                <span key={type} data-testid={`${type}-status`} data-acknowledged={alert ? 'true' : undefined} title={alert ? 'Acknowledged' : undefined} className={chipClass}>
                                                        {content}
                                                </span>
                                        );
                                })}
                        </div>

                        {/* Spacer */}
                        <div />

                        {/* Last updated */}
                        <div className="flex items-center gap-1 text-text-weak whitespace-nowrap pl-4">
                                <FontAwesomeIcon icon={faClock} />
                                <span>{dateTime}</span>
                        </div>

                        {/* Water + Score + ID group */}
                        <div className="flex items-center gap-3 pl-4 justify-end whitespace-nowrap">
                                {device.water_level_sensor && (
                                        <div className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faWater} className={getWaterStatus().className} />
                                                <span className={`font-semibold ${getWaterStatus().className}`}>{device.water_level}%</span>
                                        </div>
                                )}
                                <div className="flex items-baseline gap-1" data-testid="plant-score" title={SCORE_HINT}>
                                        <span className={`font-semibold ${score === null ? 'text-gray-400' : plantScoreClass(score)}`}>{score === null ? '--' : score}</span>
                                        <span className="text-xs text-text-weak">score</span>
                                </div>
                                <span className="text-xs text-text-weak"><span className="font-bold">ID</span> {device.id}</span>
                        </div>
                </div>
        );
};

export default DeviceListItem;
